import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { createSearchParams, Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Box, Container } from "@mantine/core";
import { Step, StepConnector, StepLabel, Stepper } from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
// import ContactUs2 from "../learner/course/ContactUs2";
import axios from "axios";
import classNames from "classnames";
import Swal from "sweetalert2";

import { resetUser } from "actions/auth";
import { useCertCheckout } from "@/reducers/certCheckoutSlice";

import { to_official_website_url } from "@/utilities/constant";
import { getErrorMessage } from "utilities/error";
import { removeLogGTMUser } from "utilities/googleTagManager";

import styles from "./eventCheckout.module.css";

const steps = [
	{
		label: "Delivery Details",
		route: ["/professional-cert/checkout"],
	},
	{
		label: "Payments",
		route: ["/professional-cert/checkout/omise"],
	},
	{
		label: "Successful Payment",
		route: ["/professional-cert/checkout/success"],
	},
];

export function ProfessionalCertCheckout() {
	const [searchParams] = useSearchParams();
	const certRequestId = searchParams.get("id");
	// const { id: certRequestId } = useParams();

	const { checkoutInfo, fetchCheckoutInfo } = useCertCheckout();
	const [currentStep, setCurrentStep] = React.useState(0);
	const [isReady, setIsReady] = useState(false);
	const params = Array.from(searchParams.entries());

	const invalidCheckoutInputs = !certRequestId;
	const [invalidCheckout, setInvalidCheckout] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	// Fetch and refetch all the data used for this page
	// based on the following key
	useEffect(() => {
		// payment page don't need to do anything
		if (window.location.pathname.includes("/professional-cert/checkout/omise")) {
			setIsReady(true);
			return;
		}

		if (window.location.pathname.includes("/professional-cert/checkout/success")) {
			setIsReady(true);
			return;
		}

		if (isReady && invalidCheckoutInputs && !checkoutInfo) {
			console.warn("Invalid checkout inputs");
			setInvalidCheckout(true);
			return;
		}

		if (!certRequestId) {
			console.warn("No cert request id found");
			return;
		}

		// Check if there is a regform cache in redux
		// if so we try to fetch the checkout data using the cached regform id

		// But before that we need to check if the cached regform id  + cache courserun match
		fetchCheckoutInfo(certRequestId)
			.then((checkoutData) => {
				if (!checkoutData) {
					// TODO: invalid regform or checkout , redirect back to event pages
					//  - option 1: navigate to event listing page
					//  - option 2: if have event_id navigate back to event
					//
					// return navigate(
					// 	{
					// 		pathname: "/checkout/account",
					// 		search: createSearchParams(getUrlParams(params, cachedCheckoutAnalyticsData, event_id)).toString(),
					// 	},
					// 	{ replace: true },
					// );
				}

				if (["Confirmed", "Approved"].includes(checkoutData.status)) {
					navigate("/professional-cert/checkout/success");
				}

				// prevent user manually going to success page
				if (
					location.pathname === "/professional-cert/checkout/success" &&
					checkoutData.status == "Pending"
				) {
					navigate(
						{
							pathname: "/professional-cert/checkout",
							search: createSearchParams(params).toString(),
						},
						{ replace: true },
					);
				}

				setIsReady(true);
			})
			.catch((error) => {
				console.log("error", error);
				if (axios.isAxiosError(error) && error.response?.status === 400) {
					Swal.fire({
						icon: "error",
						title: error.response?.data?.message,
						confirmButtonText: "Back to Home",
						confirmButtonAriaLabel: "Back to Home",
						confirmButtonHref: "/profile",
					}).then(() => {
						navigate("/profile");
					});
				} else {
					Swal.fire({
						icon: "error",
						title: "Oops...",
						text: "Something went wrong!",
					});
				}
			});
	}, [certRequestId]);

	useEffect(() => {
		let matchStepIndex = -1;
		steps.forEach((step, index) => {
			step.route.forEach((routeStr) => {
				if (location.pathname.startsWith(routeStr)) {
					matchStepIndex = index;
				}
			});
		});
		console.log("matchStepIndex", matchStepIndex, location.pathname);
		if (matchStepIndex >= 0) {
			setCurrentStep(matchStepIndex);
		} else {
			setCurrentStep(0);
			navigate(
				{
					pathname: "/professional-cert/checkout",
					search: location.search,
				},
				{ replace: true },
			);
		}
	}, [location]);

	/**
	 * Intercept any unauthorized request.
	 * Dispatch logout action accordingly
	 * **/
	useEffect(() => {
		const UNAUTHORIZED = 401;
		axios.interceptors.response.use(
			(response) => response,
			(error) => {
				const { status } = error.response || {};
				let error_message = getErrorMessage(error);
				if (status === UNAUTHORIZED && error_message == "Unauthenticated.") {
					removeLogGTMUser();
					dispatch(resetUser());
					localStorage.removeItem("user");
				}
				throw error;
			},
		);
	}, []);

	return (
		<div className={styles.root}>
			<Box p={20} bg="white">
				<Container size={1440}>
					<Stepper
						key={currentStep}
						alternativeLabel
						activeStep={currentStep === steps.length - 1 ? steps.length : currentStep}
						connector={<ColorlibConnector />}
						style={{
							paddingTop: 0,
							paddingBottom: 0,
							paddingLeft: 0,
							paddingRight: 0,
						}}>
						{steps.map((step) => {
							return (
								<Step key={step.label} onClick={() => {}}>
									<StepLabel StepIconComponent={ColorlibStepIcon}>{step.label}</StepLabel>
								</Step>
							);
						})}
					</Stepper>
				</Container>
			</Box>

			<Container pt={20} size={1248}>
				{isReady ? <Outlet /> : null}
			</Container>
			<Modal isOpen={invalidCheckout} centered>
				<ModalHeader>Something is wrong</ModalHeader>
				<ModalBody>Invalid Checkout</ModalBody>
				<ModalFooter>
					<a href={to_official_website_url("/")}>
						<Button color="primary">Back to Home</Button>
					</a>
				</ModalFooter>
			</Modal>
		</div>
	);
}

const useColorlibStepIconStyles = makeStyles({
	root: {
		backgroundColor: "#EEEEEE",
		zIndex: 1,
		color: "gray",
		border: "3px solid #ddd",
		width: 40,
		height: 40,
		display: "flex",
		borderRadius: "50%",
		justifyContent: "center",
		alignItems: "center",
		fontWeight: "bold",
	},
	active: {
		// backgroundImage: "linear-gradient( 90deg, #4BA58C 0%,  #3B85C4 100%)",
		backgroundColor: "white",
		boxShadow: "0 0 2px 2px rgba(0,0,0,.25)",
		color: "#4BA58C",
		border: "3px solid #54C49C",
	},
	completed: {
		backgroundImage: "linear-gradient( 90deg, #4BA58C 0%,  #3B85C4 100%)",
		border: "none",
		color: "white",
		boxShadow: "0 0 4px 4px rgba(0,0,0,.25)",
	},
});

const ColorlibConnector = withStyles({
	alternativeLabel: {
		top: 18,
	},
	active: {
		"& $line": {
			backgroundImage: "linear-gradient( 95deg, #4BA58C 0%, #3B85C4 100%)",
		},
	},
	completed: {
		"& $line": {
			backgroundImage: "linear-gradient( 95deg, #4BA58C 0%, #3B85C4 100%)",
		},
	},
	line: {
		height: 5,
		border: 0,
		backgroundColor: "#eaeaf0",
		borderRadius: 1,
	},
})(StepConnector);

function ColorlibStepIcon(props) {
	const classes = useColorlibStepIconStyles();
	const { active, completed } = props;

	return (
		<div
			className={`${classNames(classes.root, {
				[classes.active]: active,
				[classes.completed]: completed,
				[styles.glowing_circle]: completed,
			})}`}>
			{props.icon}
		</div>
	);
}
